import React, { forwardRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface NumberInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outline' | 'filled';
  error?: boolean;
  disabled?: boolean;
  placeholder?: string;
  step?: string | number;
  min?: string | number;
  max?: string | number;
}

const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      value,
      onChange,
      className = '',
      size = 'md',
      variant = 'default',
      error = false,
      disabled = false,
      placeholder,
      step = '0.00001',
      min,
      max,
      ...props
    },
    ref
  ) => {
    const { theme } = useTheme();

    // Size classes
    const sizeClasses = {
      sm: 'px-2 py-1 text-xs',
      md: 'px-3 py-2 text-xs',
      lg: 'px-4 py-3 text-xs',
    };

    // Variant classes
    const variantClasses = {
      default: theme === 'dark' 
        ? 'bg-gray-700 border-gray-600 text-white focus:ring-[#2AB7A6] focus:border-[#2AB7A6]' 
        : 'bg-white border-gray-300 text-gray-900 focus:ring-[#2AB7A6] focus:border-[#2AB7A6]',
      outline: theme === 'dark'
        ? 'bg-transparent border-gray-600 text-white focus:ring-[#2AB7A6] focus:border-[#2AB7A6]'
        : 'bg-transparent border-gray-300 text-gray-900 focus:ring-[#2AB7A6] focus:border-[#2AB7A6]',
      filled: theme === 'dark'
        ? 'bg-gray-800 border-gray-700 text-white focus:ring-[#2AB7A6] focus:border-[#2AB7A6]'
        : 'bg-gray-50 border-gray-200 text-gray-900 focus:ring-[#2AB7A6] focus:border-[#2AB7A6]',
    };

    // Error classes
    const errorClasses = error
      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
      : '';

    // Disabled classes
    const disabledClasses = disabled
      ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800'
      : '';

    const baseClasses = `
      rounded border transition-all duration-200 focus:outline-none focus:ring-1
      [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
      [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:m-0
      ${sizeClasses[size]}
      ${variantClasses[variant]}
      ${errorClasses}
      ${disabledClasses}
      ${className}
    `.trim();

    return (
      <input
        ref={ref}
        type="number"
        value={value}
        onChange={onChange}
        className={baseClasses}
        placeholder={placeholder}
        step={step}
        min={min}
        max={max}
        disabled={disabled}
        style={{
          MozAppearance: 'textfield',
          WebkitAppearance: 'none',
          appearance: 'none',
        }}
        {...props}
      />
    );
  }
);

NumberInput.displayName = 'NumberInput';

export default NumberInput;
